<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-2 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 -translate-y-2 scale-95"
  >
    <div v-if="visible" :class="[wrapperClass, `base-alert--${variant}`]" role="alert">
      <div class="flex items-start gap-3">
        <!-- Icon -->
        <span class="base-alert-icon shrink-0 mt-0.5">
          <CheckCircle2 v-if="variant === 'success'" class="h-5 w-5" />
          <XCircle v-else-if="variant === 'error'" class="h-5 w-5" />
          <AlertTriangle v-else-if="variant === 'warning'" class="h-5 w-5" />
          <Info v-else class="h-5 w-5" />
        </span>

        <div class="flex-1 min-w-0">
          <p v-if="title" class="text-sm font-semibold">{{ title }}</p>
          <p class="text-sm" :class="{ 'mt-0.5': title }">
            <slot />
          </p>
        </div>

        <!-- Close button -->
        <button
          v-if="dismissible"
          type="button"
          class="base-alert-close shrink-0 ml-auto -mr-1 -mt-1 p-1 rounded-lg transition-colors duration-150"
          :class="closeButtonClass"
          @click="$emit('dismiss')"
        >
          <span class="sr-only">Dismiss</span>
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  variant?: 'info' | 'success' | 'warning' | 'error'
  title?: string
  visible?: boolean
  dismissible?: boolean
  wrapperClass?: string
}>(), {
  variant: 'info',
  visible: true,
  dismissible: false
})

defineEmits<{
  dismiss: []
}>()

const closeButtonClass = computed(() => {
  const classes: Record<string, string> = {
    info: 'hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-500 dark:text-blue-400',
    success: 'hover:bg-green-100 dark:hover:bg-green-900/30 text-green-500 dark:text-green-400',
    warning: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-500 dark:text-yellow-400',
    error: 'hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400'
  }
  return classes[props.variant] || classes.info
})
</script>

<style scoped>
.base-alert--info {
  @apply bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 text-blue-800 dark:text-blue-200 rounded-xl p-4;
}

.base-alert--success {
  @apply bg-success-bg dark:bg-success/10 border border-success/30 dark:border-success-dark/30 text-success-text dark:text-success-dark rounded-xl p-4;
}

.base-alert--warning {
  @apply bg-warning-bg dark:bg-warning/10 border border-warning/30 dark:border-warning-dark/30 text-warning-text dark:text-warning-dark rounded-xl p-4;
}

.base-alert--error {
  @apply bg-danger-bg dark:bg-danger/10 border border-danger/30 dark:border-danger-dark/30 text-danger-text dark:text-danger-dark rounded-xl p-4;
}

.base-alert-icon {
  @apply text-current;
}
</style>
